import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "next/navigation";
import { forgotPassword } from "@/services/auth.service";
import {
  ForgotPasswordRequest,
  ForgotPasswordResponse,
} from "@/interface/auth.types";
import {
  forgotPasswordSchema,
  ForgotPasswordFormData,
} from "@/schema/forgot-password";

interface UseForgotPasswordProps {
  initialEmail?: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const useForgotPassword = ({
  initialEmail = "",
  onSuccess,
  onError,
}: UseForgotPasswordProps = {}) => {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: initialEmail,
    },
  });

  const submitForgotPassword = async (
    data: ForgotPasswordFormData
  ): Promise<ForgotPasswordResponse | null> => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await forgotPassword(data);
      setSuccess(true);
      setShowSuccess(true);
      // Reset form after a short delay to allow user to see success message
      setTimeout(() => {
        resetFormAfterSuccess();
      }, 2000);
      onSuccess?.();
      return response;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        "Something went wrong. Please try again.";
      setError(errorMessage);
      onError?.(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setLoading(false);
    setError(null);
    setSuccess(false);
    setShowSuccess(false);
    form.reset();
  };

  const resetFormAfterSuccess = () => {
    setSuccess(false);
    setShowSuccess(false);
    form.reset();
  };

  const handleBackToLogin = () => {
    router.push("/login");
  };

  const handleTryAgain = () => {
    setShowSuccess(false);
    resetState();
  };

  return {
    // Form methods
    form,
    submitForgotPassword,

    // State
    loading,
    error,
    success,
    showSuccess,

    // Actions
    resetState,
    resetFormAfterSuccess,
    handleBackToLogin,
    handleTryAgain,
  };
};
